import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { PopperTooltip } from "~/common/components/Tooltip";
import { Link } from "~/common/components/Link";
import { CART_PAGE, CART_STEP_2 } from "~/utils/url/staticUrls";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import ShoppingCartCheckoutOutlinedIcon from "@mui/icons-material/ShoppingCartCheckoutOutlined";
import { styled } from "~/common/mui";
import { C_PRIMARY_DISABLED } from "~/common/components/icon/Colors";


const Root = styled("div")(({ theme }) => ({
  display: "flex",
  borderRadius: 28,
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.surface.main,
  [theme.breakpoints.up("tablet")]: {
    gap: theme.spacing(2),
  },
  [theme.breakpoints.up("desktop-s")]: {
    paddingLeft: 160,
    paddingRight: 160,
  },
}));

const StepLink = styled(Link, {
  shouldForwardProp: (p) => p !== "stepState",
})<{ stepState?: "active" | "clickable" | "disabled" }>(({ theme, stepState }) => ({
  ...theme.typography.labelSmall,
  display: "flex",
  flexFlow: "column",
  gap: theme.spacing(0.5),
  padding: `${theme.spacing(1)} ${theme.spacing(0.5)}`,
  alignItems: "center",
  flex: 1,
  "& .stepIcon": {
    color: theme.palette.text.secondary,
  },
  [theme.breakpoints.up("tablet")]: {
    ...theme.typography.labelLarge,
    padding: theme.spacing(1),
    width: 200,
    flex: "none",
  },
  ...(stepState === "disabled" && {
    "& .stepName": {
      color: theme.palette.text.primary,
    },
  }),
  ...(stepState === "clickable" && {
    transition: "all 0.2s ease-in",
    "& .stepName": {
      color: theme.palette.primary.main,
    },
    "& .stepIcon": {
      color: theme.palette.primary.main,
    },
  }),
  ...(stepState === "active" && {
    "& .stepIcon": {
      color: theme.palette.primary.main,
    },
    "& .stepName": {
      color: theme.palette.primary.main,
      fontWeight: "bold",
    },
  }),
}));

const StepIcon = styled("div")(({ theme }) => ({
  "--size": "24px",
  height: "var(--size)",
  width: "var(--size)",
  fontSize: "var(--size)",
  display: "grid",
  placeItems: "center",
  "& > i": {
    fontSize: "inherit",
  },
  [theme.breakpoints.up("tablet")]: {
    "--size": "30px",
  },
}));

const StepName = styled("div")({
  textAlign: "center",
});

const DividerLine = styled("div", { shouldForwardProp: (p) => p !== "active" })<{ active?: boolean }>(
  ({ theme, active }) => ({
    height: 1,
    background: theme.palette.divider,
    flex: 1,
    alignSelf: "center",
    display: "none",
    ...(active && {
      background: C_PRIMARY_DISABLED,
    }),
    [theme.breakpoints.up("tablet")]: {
      display: "block",
    },
  }),
);

interface Props {
  className?: string;
  activeIndex?: number;
}

interface StepDef {
  nameKey: string;
  icon: ReactNode;
  activeIcon: ReactNode;
  href?: string;
}

const steps: StepDef[] = [
  {
    nameKey: "cart.b2c.checkout.step.1",
    icon: <ShoppingCartOutlinedIcon />,
    activeIcon: <ShoppingCartOutlinedIcon />,
    href: CART_PAGE,
  },
  {
    nameKey: "cart.b2c.checkout.step.2",
    icon: <FmdGoodOutlinedIcon />,
    activeIcon: <FmdGoodOutlinedIcon />,
    href: CART_STEP_2,
  },
  {
    nameKey: "cart.b2c.checkout.step.3",
    icon: <ShoppingCartCheckoutOutlinedIcon />,
    activeIcon: <ShoppingCartCheckoutOutlinedIcon />,
  },
];

export const B2CCartProcessTile = ({ className, activeIndex = -1 }: Props) => {
  const makeStep = (index: number) => (
    <SingleStep key={index} step={steps[index]} active={activeIndex === index} clickable={index < activeIndex} />
  );

  const elements = [makeStep(0)];
  for (let i = 1; i < steps.length; i++) {
    elements.push(<DividerLine active={activeIndex >= i} key={`divider-${i}`} />);
    elements.push(makeStep(i));
  }
  return <Root className={className}>{elements}</Root>;
};

const SingleStep = ({ step, active, clickable }: { step: StepDef; active?: boolean; clickable?: boolean }) => {
  const { t } = useTranslation();
  const label = t(step.nameKey);
  const maxLabelLength = useIsMinTabletSize() ? 40 : 35;
  let labelElement;
  if (label.length <= maxLabelLength) {
    labelElement = <span>{label}</span>;
  } else {
    labelElement = (
      <PopperTooltip tooltipContent={label} placement={"bottom"}>
        <div>{label.substring(0, maxLabelLength) + "..."}</div>
      </PopperTooltip>
    );
  }
  const stepState = active ? "active" : clickable ? "clickable" : "disabled";
  return (
    <StepLink disabled={!clickable} href={step.href} stepState={stepState}>
      <StepIcon className="stepIcon">{active || clickable ? step.activeIcon : step.icon}</StepIcon>
      <StepName className="stepName">{labelElement}</StepName>
    </StepLink>
  );
};
